/**
 * Tells the other tabs of this browser that the session changed (ADR-0302).
 *
 * The session cookie is shared by every tab of the origin, so a switch, sign-in
 * or sign-out in one tab changes what every other tab's requests are answered
 * as. This channel lets those tabs notice at once instead of on their next
 * request. It is a signal only: the BFF's tab-account check is what actually
 * refuses a stale tab, whether or not a message arrived.
 */

export type SessionEvent =
  | { type: "account-switched"; accountPublicId: string }
  | { type: "signed-in"; userPublicId: string; accountPublicId: string }
  | { type: "signed-out" };

type SessionListener = (event: SessionEvent) => void;

const CHANNEL_NAME = "verity-session";
/** The `storage`-event fallback for browsers without BroadcastChannel. */
const STORAGE_KEY = "verity-session-event";

let channel: BroadcastChannel | null | undefined;

function getChannel(): BroadcastChannel | null {
  if (channel === undefined) {
    channel = typeof BroadcastChannel === "function" ? new BroadcastChannel(CHANNEL_NAME) : null;
  }
  return channel;
}

function isSessionEvent(value: unknown): value is SessionEvent {
  if (!value || typeof value !== "object") return false;
  const event = value as Record<string, unknown>;
  switch (event.type) {
    case "account-switched":
      return typeof event.accountPublicId === "string";
    case "signed-in":
      return typeof event.userPublicId === "string" && typeof event.accountPublicId === "string";
    case "signed-out":
      return true;
    default:
      return false;
  }
}

/** Tell the other tabs. The posting tab does not receive its own message. */
export function postSessionEvent(event: SessionEvent): void {
  const bc = getChannel();
  if (bc) {
    bc.postMessage(event);
    return;
  }
  try {
    // A nonce makes every write a change, so a repeated event still fires.
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ event, nonce: `${Date.now()}-${Math.random()}` }),
    );
  } catch {
    // Storage blocked (private mode, quota): the BFF check still protects the tab.
  }
}

export function subscribeSessionEvents(listener: SessionListener): () => void {
  const bc = getChannel();
  if (bc) {
    const onMessage = (message: MessageEvent) => {
      if (isSessionEvent(message.data)) listener(message.data);
    };
    bc.addEventListener("message", onMessage);
    return () => bc.removeEventListener("message", onMessage);
  }

  const onStorage = (storage: StorageEvent) => {
    if (storage.key !== STORAGE_KEY || !storage.newValue) return;
    try {
      const parsed = JSON.parse(storage.newValue) as { event?: unknown };
      if (isSessionEvent(parsed.event)) listener(parsed.event);
    } catch {
      // Not ours or corrupt: ignore.
    }
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}

/** Test seam: forget the cached channel so a test can swap the global. */
export function resetSessionChannelForTests(): void {
  channel?.close();
  channel = undefined;
}
